import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../../tokens";
import { useAuthStore } from "../../store";
import { UserDetail } from "./UserDetail";
import { Badge } from "../../components/Badge";

type UserRow = {
  id: string;
  name: string;
  email: string;
  segment: string | null;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  resumeCount: number;
  matchCount: number;
  /** Partner attribution (e.g. 'exh1b') when provisioned via the Partner API; null for direct signups. */
  partnerSource: string | null;
};

type UsersPage = {
  items: UserRow[];
  total: number;
  page: number;
  limit: number;
};

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

export function AdminUsers() {
  const token = useAuthStore((s) => s.token);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const q = useQuery<UsersPage>({
    queryKey: ["admin-users", debouncedSearch, page],
    queryFn: async () => {
      const { data } = await axios.get("/api/admin/users", {
        params: { page, limit: 50, search: debouncedSearch },
      });
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(v), 350);
  };

  const totalPages = q.data ? Math.ceil(q.data.total / q.data.limit) : 0;

  return (
    <div>
      {/* Search bar */}
      <div style={{ marginBottom: "16px" }}>
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "8px 12px", background: T.s2, border: `1px solid ${T.b1}`,
            borderRadius: "6px", color: T.t0, fontFamily: T.body, fontSize: "13px",
            outline: "none",
          }}
        />
      </div>

      {q.isLoading && (
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading…</p>
      )}
      {q.isError && (
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.err }}>Failed to load users.</p>
      )}

      {q.data && (
        <>
          <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
            {q.data.total} user{q.data.total !== 1 ? "s" : ""}
            {debouncedSearch && ` matching "${debouncedSearch}"`}
          </div>

          {/* Table */}
          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 52px 48px 70px 52px 52px",
              padding: "8px 16px", borderBottom: `1px solid ${T.b0}`,
              fontFamily: T.mono, fontSize: "9px", color: T.t3,
              letterSpacing: "0.10em", textTransform: "uppercase" }}>
              <span>Name</span>
              <span>Email</span>
              <span>Segment</span>
              <span style={{ textAlign: "center" }}>✉</span>
              <span style={{ textAlign: "center" }}>Active</span>
              <span>Joined</span>
              <span style={{ textAlign: "right" }}>CVs</span>
              <span style={{ textAlign: "right" }}>Matches</span>
            </div>

            {q.data.items.length === 0 && (
              <div style={{ padding: "24px 16px", fontFamily: T.body, fontSize: "13px", color: T.t3, textAlign: "center" }}>
                No users found.
              </div>
            )}

            {q.data.items.map((u, i) => (
              <div
                key={u.id}
                onClick={() => setSelectedId(u.id)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 80px 52px 48px 70px 52px 52px",
                  padding: "10px 16px",
                  borderBottom: i < q.data!.items.length - 1 ? `1px solid ${T.b0}` : "none",
                  cursor: "pointer",
                  background: selectedId === u.id ? T.s2 : "transparent",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { if (selectedId !== u.id) (e.currentTarget as HTMLDivElement).style.background = T.s2; }}
                onMouseLeave={(e) => { if (selectedId !== u.id) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
              >
                <span style={{ fontFamily: T.body, fontSize: "13px", color: T.t0,
                  display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.name}
                  </span>
                  {u.partnerSource && (
                    <Badge variant="teal" size="sm" style={{ flexShrink: 0 }}>{u.partnerSource}</Badge>
                  )}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {u.email}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {u.segment ?? "—"}
                </span>
                <span style={{ textAlign: "center", fontSize: "11px",
                  color: u.emailVerified ? T.ok : T.t3 }}>
                  {u.emailVerified ? "✓" : "–"}
                </span>
                <span style={{ textAlign: "center", fontSize: "11px",
                  color: u.isActive ? T.ok : T.err }}>
                  {u.isActive ? "✓" : "✗"}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2 }}>
                  {fmt(u.createdAt)}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: "12px", color: T.t1,
                  textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {u.resumeCount}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: "12px",
                  color: u.matchCount > 0 ? T.tealSoft : T.t3,
                  textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {u.matchCount}
                </span>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center",
              marginTop: "12px", justifyContent: "flex-end" }}>
              <button
                type="button" disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                style={{ padding: "5px 10px", background: T.s2, border: `1px solid ${T.b1}`,
                  borderRadius: "5px", color: page <= 1 ? T.t3 : T.t0,
                  fontFamily: T.body, fontSize: "12px", cursor: page <= 1 ? "default" : "pointer" }}
              >‹ Prev</button>
              <span style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2 }}>
                {page} / {totalPages}
              </span>
              <button
                type="button" disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ padding: "5px 10px", background: T.s2, border: `1px solid ${T.b1}`,
                  borderRadius: "5px", color: page >= totalPages ? T.t3 : T.t0,
                  fontFamily: T.body, fontSize: "12px", cursor: page >= totalPages ? "default" : "pointer" }}
              >Next ›</button>
            </div>
          )}
        </>
      )}

      {/* Detail panel */}
      {selectedId && (
        <UserDetail userId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}
